import { Flex, Text, Card } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import {
  EditableTable,
  EditableTableField,
  EditableTableRow,
} from "@/v2/components/editable-table";

const fields: EditableTableField[] = [
  {
    key: "key",
    label: "Key",
    type: "text",
    placeholder: "Header name (e.g., Content-Type)",
    required: true,
  },
  {
    key: "value",
    label: "Value",
    type: "text",
    placeholder: "Header value",
    required: true,
  },
];

interface HeadersProps {
  headers?: Record<string, string>;
  onHeadersChange: (headers: Record<string, string>) => void;
}

export function Headers({ headers = {}, onHeadersChange }: HeadersProps) {
  const tableValue: EditableTableRow[] = useMemo(() => {
    return Object.entries(headers).map(([key, value]) => ({
      id: `header_${key}_${Date.now()}`,
      key,
      value,
      enabled: true,
    }));
  }, [headers]);

  // Convert table changes back to headers format
  const handleTableChange = useCallback(
    (newRows: EditableTableRow[]) => {
      // Only include rows that are enabled AND have a non-empty key
      const enabledHeaders = newRows.filter(
        (row) => row.enabled && row.key && row.key.trim() !== ""
      );
      const headersObj = enabledHeaders.reduce((acc, row) => {
        acc[row.key] = row.value;
        return acc;
      }, {} as Record<string, string>);
      onHeadersChange(headersObj);
    },
    [onHeadersChange]
  );

  return (
    <div className="h-full">
      <Flex direction="column" gap="4">
        <EditableTable
          value={tableValue}
          onChange={handleTableChange}
          fields={fields}
          title="Headers"
          addButtonText="Add Header"
          emptyStateText="No headers added yet"
          showToggle={true}
        />

        <Card className="p-3 bg-blue-2 border-blue-6">
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium" color="blue">
              Common Headers
            </Text>
            <Text size="1" color="blue">
              Here are some commonly used headers you might want to add:
            </Text>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="text-xs">
                <Text weight="medium">Content-Type</Text>
                <Text color="gray">application/json</Text>
              </div>
              <div className="text-xs">
                <Text weight="medium">Accept</Text>
                <Text color="gray">application/json</Text>
              </div>
              <div className="text-xs">
                <Text weight="medium">User-Agent</Text>
                <Text color="gray">MyApp/1.0</Text>
              </div>
              <div className="text-xs">
                <Text weight="medium">Cache-Control</Text>
                <Text color="gray">no-cache</Text>
              </div>
            </div>
          </Flex>
        </Card>
      </Flex>
    </div>
  );
}
